import React from "react";
import { Pencil, Trash2, Star, ExternalLink, Users, Share2, Copy } from "lucide-react";
import { Link } from "react-router-dom";

const PUBLIC_PATH = { events: "/events", experiences: "/experiences", businesses: "/directory", guides: "/guides", ads: "/#sponsored" };

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

const AttendeesBtn = ({ show, onClick, id }) => {
  if (!show) return null;
  return <button onClick={onClick} data-testid={`admin-attendees-${id}`} title="Who's coming" className="p-2 rounded-full hover:bg-[color:var(--sand)] text-[color:var(--ocean)]"><Users size={15} /></button>;
};

const CaptionBtn = ({ show, onClick, id }) => {
  if (!show) return null;
  return <button onClick={onClick} data-testid={`admin-caption-${id}`} title="Social captions" className="p-2 rounded-full hover:bg-[color:var(--sand)]"><Share2 size={15} /></button>;
};

export const AdminTable = ({ sectionKey, section, items, onEdit, onDelete, onAttendees, onCaption, onDuplicate }) => {
  if (items.length === 0)
    return <div data-testid="admin-empty" className="py-16 text-center text-[color:var(--ink-2)]">Nothing here yet.</div>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-[color:var(--line)] bg-white">
      <table className="w-full text-sm" data-testid={`admin-table-${sectionKey}`}>
        <thead className="text-left text-[0.68rem] uppercase tracking-[0.14em] text-[color:var(--ink-2)] border-b border-[color:var(--line)]">
          <tr>
            <th className="px-4 py-3 font-bold">Item</th>
            <th className="px-4 py-3 font-bold">{sectionKey === "ads" ? "Tiles" : "Category"}</th>
            {sectionKey === "events" && <th className="px-4 py-3 font-bold">Date</th>}
            {sectionKey === "ads" && <th className="px-4 py-3 font-bold">Runs</th>}
            <th className="px-4 py-3 font-bold">Status</th>
            <th className="px-4 py-3 font-bold text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} data-testid={`admin-row-${it.id}`} className="border-b border-[color:var(--line)] last:border-0 hover:bg-[color:var(--sand)]/40">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <img src={it.image || it.cover_image || it.logo || it.tiles?.[0]?.img} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0 bg-[color:var(--sand)]" />
                  <div>
                    <div className="font-semibold text-[color:var(--ocean)] flex items-center gap-1.5">
                      {it[section.titleKey]}
                      {it.featured && <Star size={12} className="fill-[color:var(--mango)] text-[color:var(--mango)]" />}
                    </div>
                    <div className="text-xs text-[color:var(--ink-2)]">{sectionKey === "ads" ? `${it.impressions || 0} views · ${it.clicks || 0} clicks` : it.suburb || it.location || it.author || ""}</div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3"><span className="chip">{sectionKey === "ads" ? `${(it.tiles || []).length} tiles` : it.category}</span></td>
              {sectionKey === "events" && <td className="px-4 py-3 whitespace-nowrap">{fmt(it.start_date)}</td>}
              {sectionKey === "ads" && <td className="px-4 py-3 whitespace-nowrap text-xs">{it.start_date || it.end_date ? `${it.start_date ? fmt(it.start_date) : "now"} → ${it.end_date ? fmt(it.end_date) : "∞"}` : "Always"}</td>}
              <td className="px-4 py-3">
                {it.published === false
                  ? <span className="chip !bg-amber-100 !text-amber-800 !border-amber-200">{sectionKey === "ads" ? "Paused" : "Draft · hidden"}</span>
                  : it.is_past
                  ? <span className="chip !bg-[color:var(--ink-2)]/10">{sectionKey === "ads" ? "Ended" : "Past · hidden"}</span>
                  : it.is_scheduled
                  ? <span className="chip !bg-sky-100 !text-sky-800 !border-sky-200">Scheduled</span>
                  : <span className="chip !bg-emerald-100 !text-emerald-800 !border-emerald-200">Live{it.recurrence && it.recurrence !== "none" ? ` · ${it.recurrence}` : ""}</span>}
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-1">
                  <Link to={`${PUBLIC_PATH[sectionKey]}${sectionKey === "ads" ? "" : `/${it.slug}`}`} target="_blank" title="View on site" className="p-2 rounded-full hover:bg-[color:var(--sand)]"><ExternalLink size={15} /></Link>
                  <AttendeesBtn show={sectionKey === "events"} onClick={() => onAttendees(it)} id={it.id} />
                  <CaptionBtn show={sectionKey !== "ads"} onClick={() => onCaption(it)} id={it.id} />
                  <button onClick={() => onDuplicate(it)} data-testid={`admin-duplicate-${it.id}`} title="Duplicate" className="p-2 rounded-full hover:bg-[color:var(--sand)]"><Copy size={15} /></button>
                  <button onClick={() => onEdit(it)} data-testid={`admin-edit-${it.id}`} title="Edit" className="p-2 rounded-full hover:bg-[color:var(--sand)]"><Pencil size={15} /></button>
                  <button onClick={() => onDelete(it)} data-testid={`admin-delete-${it.id}`} title="Delete" className="p-2 rounded-full hover:bg-red-50 text-red-600"><Trash2 size={15} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
